import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Loader2, Power, Pencil, Tag, Clock } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { notify } from '../../lib/notify';
import { Modal } from '../../components/Modal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useAuthStore } from '../../lib/auth-store';

interface Category {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  sla_first_response_minutes: number | null;
  sla_resolution_minutes: number | null;
  is_active: boolean;
}

const createSchema = z.object({
  slug: z
    .string()
    .trim()
    .min(2, 'Mínimo 2 caracteres.')
    .max(80, 'Máximo 80 caracteres.')
    .regex(/^[a-z0-9-]+$/, 'Solo minúsculas, números y guiones.'),
  name: z.string().trim().min(2, 'Mínimo 2 caracteres.').max(150),
  description: z.string().max(500).optional(),
  sla_first_response_minutes: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .or(z.literal('').transform(() => undefined)),
  sla_resolution_minutes: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .or(z.literal('').transform(() => undefined)),
});

const updateSchema = z.object({
  name: z.string().trim().min(2).max(150),
  description: z.string().max(500).optional(),
  sla_first_response_minutes: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .or(z.literal('').transform(() => undefined)),
  sla_resolution_minutes: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .or(z.literal('').transform(() => undefined)),
});

type CreateInput = z.input<typeof createSchema>;
type UpdateInput = z.input<typeof updateSchema>;

function formatSlaMinutes(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} h`;
  return `${Math.round(minutes / 1440)} d`;
}

export default function Categories() {
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role === 'admin';

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Category | null>(null);
  const [toggleTarget, setToggleTarget] = useState<Category | null>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    apiFetch<Category[]>('/categories')
      .then(setCategories)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [reloadTick]);

  const refresh = () => setReloadTick((t) => t + 1);

  const doToggle = async () => {
    if (!toggleTarget) return;
    try {
      await apiFetch(`/categories/${toggleTarget.id}`, {
        method: toggleTarget.is_active ? 'DELETE' : 'PATCH',
        body: toggleTarget.is_active ? undefined : { is_active: true },
      });
      notify.success(
        toggleTarget.is_active ? 'Categoría desactivada.' : 'Categoría reactivada.',
        toggleTarget.name,
      );
      refresh();
    } catch (err) {
      notify.apiError(err, 'No se pudo cambiar el estado.');
      throw err;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Categorías</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Clasifican los tickets y permiten configurar SLAs específicos por tipo de caso.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition"
          >
            <Plus size={16} /> Nueva categoría
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="animate-spin" size={20} />
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-rose-600">{error}</div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <Tag className="mx-auto mb-2 text-slate-300" size={32} />
            Aún no hay categorías registradas.
            {canEdit && ' Crea la primera para clasificar tus tickets.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Nombre</th>
                  <th className="text-left px-4 py-2.5 font-medium">Slug</th>
                  <th className="text-left px-4 py-2.5 font-medium">SLA 1ra resp.</th>
                  <th className="text-left px-4 py-2.5 font-medium">SLA resolución</th>
                  <th className="text-left px-4 py-2.5 font-medium">Estado</th>
                  {canEdit && <th className="text-right px-4 py-2.5 font-medium">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {categories.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <div className="text-slate-900 font-medium">{c.name}</div>
                      {c.description && (
                        <div className="text-xs text-slate-500 mt-0.5 max-w-md truncate">
                          {c.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 font-mono text-xs">{c.slug}</td>
                    <td className="px-4 py-2.5 text-slate-700 text-xs">
                      <Clock size={12} className="inline mr-1 text-slate-400" />
                      {formatSlaMinutes(c.sla_first_response_minutes)}
                    </td>
                    <td className="px-4 py-2.5 text-slate-700 text-xs">
                      <Clock size={12} className="inline mr-1 text-slate-400" />
                      {formatSlaMinutes(c.sla_resolution_minutes)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.is_active
                            ? 'text-emerald-700 bg-emerald-50'
                            : 'text-slate-500 bg-slate-100'
                        }`}
                      >
                        {c.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    {canEdit && (
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <button
                            onClick={() => setEditTarget(c)}
                            title="Editar"
                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => setToggleTarget(c)}
                            title={c.is_active ? 'Desactivar' : 'Reactivar'}
                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                          >
                            <Power size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canEdit && (
        <CreateCategoryModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(name) => {
            setCreateOpen(false);
            notify.success('Categoría creada.', name);
            refresh();
          }}
        />
      )}

      {canEdit && editTarget && (
        <EditCategoryModal
          category={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={(name) => {
            setEditTarget(null);
            notify.success('Categoría actualizada.', name);
            refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={toggleTarget !== null}
        onOpenChange={(o) => !o && setToggleTarget(null)}
        title={toggleTarget?.is_active ? 'Desactivar categoría' : 'Reactivar categoría'}
        description={
          toggleTarget && (
            <>
              Categoría: <strong>{toggleTarget.name}</strong>.
              {toggleTarget.is_active
                ? ' Los tickets existentes con esta categoría no se modifican; simplemente no aparecerá en los selectores para nuevos tickets.'
                : ' Volverá a estar disponible al crear o editar tickets.'}
            </>
          )
        }
        confirmLabel={toggleTarget?.is_active ? 'Desactivar' : 'Reactivar'}
        onConfirm={doToggle}
      />
    </div>
  );
}

function CreateCategoryModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (name: string) => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateInput>({ resolver: zodResolver(createSchema) });

  const onSubmit = async (data: CreateInput) => {
    try {
      await apiFetch('/categories', {
        method: 'POST',
        body: {
          ...data,
          description: data.description || null,
          sla_first_response_minutes: data.sla_first_response_minutes || null,
          sla_resolution_minutes: data.sla_resolution_minutes || null,
        },
      });
      reset();
      onCreated(data.name);
    } catch (err) {
      notify.apiError(err, 'No se pudo crear la categoría.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Nueva categoría"
      description="Las categorías se usan para clasificar tickets y configurar SLAs específicos."
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Field label="Nombre" error={errors.name?.message}>
          <input
            type="text"
            placeholder="Ej. Bug en producción"
            {...register('name')}
            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </Field>
        <Field
          label="Slug"
          hint="Identificador único en URLs y búsquedas. Solo minúsculas, números y guiones."
          error={errors.slug?.message}
        >
          <input
            type="text"
            placeholder="bug-produccion"
            {...register('slug')}
            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
          />
        </Field>
        <Field label="Descripción" hint="Opcional. Visible al asignar la categoría." error={errors.description?.message}>
          <textarea
            placeholder="Cuándo usar esta categoría…"
            {...register('description')}
            rows={2}
            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label="SLA 1ra resp. (min)"
            hint="Opcional. Reemplaza el SLA default."
            error={errors.sla_first_response_minutes?.message}
          >
            <input
              type="number"
              min="1"
              placeholder="60"
              {...register('sla_first_response_minutes')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Field>
          <Field
            label="SLA resolución (min)"
            hint="Opcional."
            error={errors.sla_resolution_minutes?.message}
          >
            <input
              type="number"
              min="1"
              placeholder="480"
              {...register('sla_resolution_minutes')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="px-3 py-1.5 text-sm rounded-lg text-slate-700 hover:bg-slate-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition disabled:opacity-50"
          >
            {isSubmitting && <Loader2 size={14} className="animate-spin" />}
            Crear
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditCategoryModal({
  category,
  onClose,
  onUpdated,
}: {
  category: Category;
  onClose: () => void;
  onUpdated: (name: string) => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UpdateInput>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      name: category.name,
      description: category.description ?? undefined,
      sla_first_response_minutes: category.sla_first_response_minutes ?? undefined,
      sla_resolution_minutes: category.sla_resolution_minutes ?? undefined,
    },
  });

  const onSubmit = async (data: UpdateInput) => {
    try {
      await apiFetch(`/categories/${category.id}`, {
        method: 'PATCH',
        body: {
          ...data,
          description: data.description || null,
          sla_first_response_minutes: data.sla_first_response_minutes || null,
          sla_resolution_minutes: data.sla_resolution_minutes || null,
        },
      });
      onUpdated(data.name as string);
    } catch (err) {
      notify.apiError(err, 'No se pudo actualizar la categoría.');
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title="Editar categoría"
      description={`Slug: ${category.slug} (no se puede modificar)`}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Field label="Nombre" error={errors.name?.message}>
          <input
            type="text"
            {...register('name')}
            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </Field>
        <Field label="Descripción" error={errors.description?.message}>
          <textarea
            {...register('description')}
            rows={2}
            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="SLA 1ra resp. (min)" error={errors.sla_first_response_minutes?.message}>
            <input
              type="number"
              min="1"
              {...register('sla_first_response_minutes')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Field>
          <Field label="SLA resolución (min)" error={errors.sla_resolution_minutes?.message}>
            <input
              type="number"
              min="1"
              {...register('sla_resolution_minutes')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg text-slate-700 hover:bg-slate-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition disabled:opacity-50"
          >
            {isSubmitting && <Loader2 size={14} className="animate-spin" />}
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-700">{label}</label>
      {children}
      {hint && !error && <p className="text-[11px] text-slate-500">{hint}</p>}
      {error && <p className="text-[11px] text-rose-600">{error}</p>}
    </div>
  );
}
